import { useState, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Avatar,
  Divider,
  IconButton,
  Grid,
  Paper,
  ImageList,
  ImageListItem
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import SendIcon from '@mui/icons-material/Send'
import { useInternalComments, useAddInternalComment } from '@services/helpdesk/helpdesk.hooks'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { PhotoUpload } from '@services/helpdesk/helpdesk.service'
import UploadProgress from '@components/UploadProgress'

interface HelpdeskInternalCommentsProps {
  ticketId: number
}

const HelpdeskInternalComments = ({ ticketId }: HelpdeskInternalCommentsProps) => {
  const { data: commentsData, isLoading, error } = useInternalComments(ticketId)
  const {
    mutate: addComment,
    isPending: isAddingComment,
    error: addError
  } = useAddInternalComment()

  // Get comments array from paginated response
  const comments = commentsData?.results || []

  const [newComment, setNewComment] = useState('')
  const [photoUploads, setPhotoUploads] = useState<PhotoUpload[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewComment(event.target.value)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newUploads: PhotoUpload[] = []

      for (let i = 0; i < event.target.files.length; i++) {
        const file = event.target.files[i]
        newUploads.push({
          file,
          comment: ''
        })
      }

      setPhotoUploads([...photoUploads, ...newUploads])
    }
  }

  const handlePhotoCommentChange = (index: number, comment: string) => {
    const updatedUploads = [...photoUploads]
    updatedUploads[index].comment = comment
    setPhotoUploads(updatedUploads)
  }

  const handleRemovePhoto = (index: number) => {
    const updatedUploads = [...photoUploads]
    updatedUploads.splice(index, 1)
    setPhotoUploads(updatedUploads)
  }

  const handleSubmit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault()
    }

    if (newComment.trim() || photoUploads.length > 0) {
      addComment(
        {
          ticketId,
          comment: newComment,
          photoUploads
        },
        {
          onSuccess: () => {
            setNewComment('')
            setPhotoUploads([])
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
          },
          onError: (error) => {
            console.error('Error adding comment:', error)
            alert(`Error adding comment: ${(error as Error).message}`)
          }
        }
      )
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && event.ctrlKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ my: 2 }}>
        Błąd podczas ładowania komentarzy: {(error as Error).message}
      </Alert>
    )
  }

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Komentarze wewnętrzne
      </Typography>

      {comments.length === 0 ? (
        <Alert severity='info' sx={{ my: 2 }}>
          Brak komentarzy wewnętrznych dla tego zgłoszenia.
        </Alert>
      ) : (
        <Box sx={{ mb: 4 }}>
          {comments.map((comment) => (
            <Card key={comment.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    {comment.author
                      ? `${comment.author.firstName.charAt(0)}${comment.author.lastName.charAt(0)}`
                      : 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant='subtitle1'>
                      {comment.author
                        ? `${comment.author.firstName} ${comment.author.lastName}`
                        : 'Nieznany użytkownik'}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {format(new Date(comment.createdOn), 'dd.MM.yyyy HH:mm', { locale: pl })}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant='body1' sx={{ mb: 2 }}>
                  {comment.comment}
                </Typography>

                {comment.photos && comment.photos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Załączniki:
                    </Typography>
                    <ImageList cols={3} rowHeight={164} gap={8}>
                      {comment.photos.map((photo) => (
                        <ImageListItem key={photo.id}>
                          <img
                            src={photo.file}
                            alt={photo.name || 'Zdjęcie'}
                            loading='lazy'
                            style={{ borderRadius: 4 }}
                          />
                          {photo.comment && (
                            <Typography variant='caption' sx={{ mt: 0.5, display: 'block' }}>
                              {photo.comment}
                            </Typography>
                          )}
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant='h6' gutterBottom>
        Dodaj komentarz
      </Typography>

      <Paper elevation={0} variant='outlined' sx={{ p: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder='Wpisz komentarz...'
            value={newComment}
            onChange={handleCommentChange}
            onKeyDown={handleKeyDown}
            sx={{ mb: 2 }}
            disabled={isAddingComment}
          />

          {photoUploads.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' gutterBottom>
                Załączniki:
              </Typography>
              <Grid container spacing={2}>
                {photoUploads.map((upload, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper variant='outlined' sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant='body2'
                          sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {upload.file.name}
                        </Typography>
                        <IconButton
                          size='small'
                          onClick={() => handleRemovePhoto(index)}
                          disabled={isAddingComment}
                        >
                          ×
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        size='small'
                        placeholder='Komentarz do zdjęcia'
                        value={upload.comment}
                        onChange={(e) => handlePhotoCommentChange(index, e.target.value)}
                        disabled={isAddingComment}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {addError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              Błąd podczas dodawania komentarza: {(addError as Error).message}
            </Alert>
          )}

          <UploadProgress />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type='button'
              startIcon={<AttachFileIcon />}
              onClick={handleAttachClick}
              disabled={isAddingComment}
            >
              Dodaj zdjęcie
            </Button>
            <Button
              type='submit'
              variant='contained'
              endIcon={<SendIcon />}
              disabled={isAddingComment || (!newComment.trim() && photoUploads.length === 0)}
            >
              {isAddingComment ? <CircularProgress size={24} /> : 'Wyślij'}
            </Button>
          </Box>

          <input
            type='file'
            accept='image/*'
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </form>
      </Paper>
    </Box>
  )
}

export default HelpdeskInternalComments
